import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notify } from 'messages';

import { deletePythonLibrary as deletePythonLibraryRequest } from 'api/python_libraries/endpoints/deletePythonLibrary';
import { getPythonLibraries, type PythonLibraryRow } from 'api/python_libraries/endpoints/getPythonLibraries';
import { postPythonLibrary, type SavePythonLibrary } from 'api/python_libraries/endpoints/postPythonLibrary';
import { putPythonLibrary } from 'api/python_libraries/endpoints/putPythonLibrary';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

import { toDraft } from './toDraft';
import { acceptDegraded } from 'validation';

interface PythonLibrariesState {
  // Every writer stores a boundary-validated list row: the read populates the array and the
  // delete filters it. Create and update deliberately DON'T write here — their responses are a
  // different, lighter shape (no `content`, from their own RETURNING clauses), and the edit modal
  // is opened through the action registry, which refetches on close (`.then(ctx.refresh)`). That
  // refetch is what surfaces a new or edited library, and it is what makes this type honest for
  // the whole array rather than for the rows that happen to have come from the read.
  items: PythonLibraryRow[];
  // The LIST's load error, and only that — `ListPythonLibraries` hands it to
  // `TableViewLayout`, which renders it as a persistent banner on the page. Write failures
  // report through their toast and must not land here: a failed save would otherwise paper the
  // list behind the open modal for the rest of the editing session. (The write `pending` cases
  // used to clear it, which only helped on the NEXT attempt — and would now wrongly clear a
  // genuine list-load error the moment a save started.)
  error: string | null;
  loading: {
    fetchPythonLibraries: boolean;
    createPythonLibrary: boolean;
    updatePythonLibrary: boolean;
    deletePythonLibrary: boolean;
  };
}

const initialState: PythonLibrariesState = {
  items: [],
  error: null,
  loading: {
    fetchPythonLibraries: false,
    createPythonLibrary: false,
    updatePythonLibrary: false,
    deletePythonLibrary: false,
  },
};

/**
 * GET /python_libraries — the whole list, name-ascending.
 *
 * Takes the list page's query payload and DISCARDS it: the backend read accepts no parameters, so
 * paging, sorting and filtering are all client-side (which is also why the page declares an empty
 * `filtersConfig` — a filter offered here would silently return the unfiltered list). The
 * parameter exists only because `useTableController` calls its `fetchData` with one; dropping it
 * from the signature would just move the cast to the call site.
 */
export const fetchPythonLibraries = createAsyncThunk<readonly PythonLibraryRow[], Record<string, unknown> | void, { rejectValue: string }>(
  'pythonLibraries/fetchPythonLibraries',
  async (_params, { rejectWithValue }) => {
    try {
      return acceptDegraded(await getPythonLibraries());
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch python libraries'));
    }
  }
);

/**
 * What a write can say beyond "it worked". Neither thunk keeps the returned record — the list is
 * refetched — but it must still keep whether the receipt was READABLE, because the two outcomes
 * ask the user for different things (#1035).
 *
 * A rejected write is a write that did not happen: the server refused the body, or no answer
 * arrived. An `unreadable` receipt is a write that DID happen and whose confirmation we could not
 * parse — the row is committed by then, so reporting it as a failure would invite a retry that,
 * for the create, inserts a second library.
 */
interface WriteOutcome {
  readonly outOfSync: boolean;
}

export const createPythonLibrary = createAsyncThunk<WriteOutcome, SavePythonLibrary, { rejectValue: string }>(
  'pythonLibraries/createPythonLibrary',
  async (save, { rejectWithValue }) => {
    try {
      const receipt = await postPythonLibrary(save);
      return { outOfSync: receipt.result !== 'ok' };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to create library'));
    }
  }
);

export const updatePythonLibrary = createAsyncThunk<WriteOutcome, { readonly id: number; readonly save: SavePythonLibrary }, { rejectValue: string }>(
  'pythonLibraries/updatePythonLibrary',
  async ({ id, save }, { rejectWithValue }) => {
    try {
      const receipt = await putPythonLibrary(id, save);
      return { outOfSync: receipt.result !== 'ok' };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to update library'));
    }
  }
);

export const deletePythonLibrary = createAsyncThunk<number, number, { rejectValue: string }>(
  'pythonLibraries/deletePythonLibrary',
  async (id, { rejectWithValue }) => {
    try {
      await deletePythonLibraryRequest(id);
      return id;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to delete library'));
    }
  }
);

const pythonLibrariesSlice = createSlice({
  name: 'pythonLibraries',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPythonLibraries.pending, (state) => {
        state.error = null;
        state.loading.fetchPythonLibraries = true;
      })
      .addCase(fetchPythonLibraries.fulfilled, (state, action) => {
        // A validated row is deeply readonly (`folder_path`), which Immer's Draft mapping rejects
        // on assignment even after spreading the outer array. `toDraft` is the sanctioned no-op
        // for exactly that; see api/README.md.
        state.items = toDraft([...action.payload]);
        state.loading.fetchPythonLibraries = false;
      })
      .addCase(fetchPythonLibraries.rejected, (state, action) => {
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch python libraries';
        state.loading.fetchPythonLibraries = false;
      })

      // Create and update only report — see the note on `items` for why neither writes a row.
      .addCase(createPythonLibrary.pending, (state) => {
        state.loading.createPythonLibrary = true;
      })
      // UNKEYED, unlike the delete messages below: one Save click is one write, so a key buys
      // nothing here and costs the case that matters — two messages under one key are one
      // message and the delivery keeps the FIRST (#1141), so a second, DIFFERENT failure inside
      // the first toast's 3s life is dropped, leaving the earlier, wrong message on screen
      // looking like the answer to the new click. `EditModel` notifies unkeyed for the same
      // reason; only slices that own their own write messages could drift from it. #757 recipe 16.
      .addCase(createPythonLibrary.fulfilled, (state, action) => {
        state.loading.createPythonLibrary = false;
        if (action.payload.outOfSync) {
          notify.warn("Python library created, but the server's response could not be read — reopen the list to confirm what was saved.");
        } else {
          notify.success('Python library created successfully!');
        }
      })
      .addCase(createPythonLibrary.rejected, (state, action) => {
        state.loading.createPythonLibrary = false;
        notify.error(`Failed to create library! ${extractErrorMessage(action.payload, 'Failed to create library')}`);
      })

      .addCase(updatePythonLibrary.pending, (state) => {
        state.loading.updatePythonLibrary = true;
      })
      .addCase(updatePythonLibrary.fulfilled, (state, action) => {
        state.loading.updatePythonLibrary = false;
        if (action.payload.outOfSync) {
          notify.warn("Python library updated, but the server's response could not be read — reopen the record to confirm what was saved.");
        } else {
          notify.success('Python library updated successfully!');
        }
      })
      .addCase(updatePythonLibrary.rejected, (state, action) => {
        state.loading.updatePythonLibrary = false;
        notify.error(`Failed to update library! ${extractErrorMessage(action.payload, 'Failed to update library')}`);
      })

      .addCase(deletePythonLibrary.pending, (state) => {
        state.loading.deletePythonLibrary = true;
      })
      // These two toasts are the ONLY feedback a delete produces. `deleteExecutor` confirms,
      // deletes and refreshes without inspecting the outcome, and `store.dispatch` of a thunk
      // never rejects — so removing them while tidying up silently removes error reporting.
      .addCase(deletePythonLibrary.fulfilled, (state, action) => {
        state.items = state.items.filter((lib) => lib.python_library_id !== action.payload);
        state.loading.deletePythonLibrary = false;
        // KEYED, unlike create/update: `deleteExecutor` confirms ONCE and then fans out one
        // dispatch per selected row (`Promise.all(entities.map)`), so a five-row delete would
        // otherwise stack five toasts for a single user action.
        const messageKey = 'data-deleted-python-library';
        if (!notify.isShowing(messageKey)) {
          notify.success('Python library deleted successfully!', { key: messageKey });
        }
      })
      .addCase(deletePythonLibrary.rejected, (state, action) => {
        state.loading.deletePythonLibrary = false;
        // Keyed for the same bulk reason as the success case above.
        const messageKey = 'data-failed-to-delete-python-library';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete library! ${extractErrorMessage(action.payload, 'Failed to delete library')}`, {
            key: messageKey,
          });
        }
      });
  },
});

export default pythonLibrariesSlice.reducer;
